/**
 * Sovereign Audio Speech-to-Bookmark Voice Dictation Engine
 * Wraps Web Speech API Recognition with smart punctuation, continuous streaming,
 * and live audio wave state telemetry.
 */

let activeRecognition = null;

export function isSpeechRecognitionSupported() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}

/**
 * Format raw speech transcript with smart punctuation and tags
 */
export function formatSpeechPunctuation(text) {
  if (!text) return '';
  let formatted = text
    .replace(/\bperiod\b/gi, '.')
    .replace(/\bcomma\b/gi, ',')
    .replace(/\bquestion mark\b/gi, '?')
    .replace(/\bexclamation mark\b/gi, '!')
    .replace(/\bnew line\b/gi, '\n')
    .replace(/\bnext line\b/gi, '\n')
    .replace(/\bhashtag\s*([a-zA-Z0-9_]+)\b/gi, '#$1')
    .replace(/\btag\s*([a-zA-Z0-9_]+)\b/gi, '#$1');

  // Capitalize first character of sentences
  formatted = formatted.replace(/(^\s*|[.!?]\s+)([a-z])/g, (_, p1, p2) => p1 + p2.toUpperCase());
  return formatted;
}

/**
 * Start listening to user voice and streams transcript
 */
export function startVoiceDictation({
  onResult,
  onError,
  onEnd,
  lang = 'en-US',
  continuous = true,
}) {
  if (!isSpeechRecognitionSupported()) {
    onError?.('Speech recognition is not supported in this browser environment.');
    return null;
  }

  stopVoiceDictation();

  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = new SpeechRecognition();

  recognition.lang = lang;
  recognition.continuous = continuous;
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;

  recognition.onresult = (event) => {
    let interimTranscript = '';
    let finalTranscript = '';

    for (let i = event.resultIndex; i < event.results.length; ++i) {
      const transcript = event.results[i][0].transcript;
      if (event.results[i].isFinal) {
        finalTranscript += formatSpeechPunctuation(transcript);
      } else {
        interimTranscript += transcript;
      }
    }

    onResult?.({
      final: finalTranscript,
      interim: interimTranscript,
      isFinal: Boolean(finalTranscript),
    });
  };

  recognition.onerror = (event) => {
    console.warn('[Voice Dictation]', event.error);
    onError?.(event.error);
  };

  recognition.onend = () => {
    activeRecognition = null;
    onEnd?.();
  };

  try {
    recognition.start();
    activeRecognition = recognition;
    return recognition;
  } catch (err) {
    onError?.(err.message);
    return null;
  }
}

/**
 * Stop active speech recognition session
 */
export function stopVoiceDictation() {
  if (activeRecognition) {
    try {
      activeRecognition.stop();
    } catch {
      // ignore
    }
    activeRecognition = null;
  }
}
